import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { arrayMove, useSortable } from "@dnd-kit/sortable";
import { api, type ApiResponse } from "@/api/client";
import { DndCSS, type DndTransform } from "@/lib/dndHelpers";
import DraggableList from "@/components/common/DraggableList";
import {
  buildTripReorderItems,
  compareUnrankedTrips,
  rankedTripIds,
  sameRanking,
  sortBySearchMatch,
} from "@ckgo2/shared";
import type { TripWithRelations } from "@ckgo2/shared/types";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  GripVertical,
  Loader2,
  Plus,
  Search,
  X,
} from "lucide-react";

/**
 * Ruční pořadí zájezdů (řez 08, vize D6) — JEDEN globální žebříček, který rozhoduje
 * o výchozím pořadí všude, kde se katalog vypisuje.
 *
 * Zápis jde přes `PATCH /api/admin/trips/reorder` a posílá VŽDY celý žebříček
 * přečíslovaný od 0 (+ vyřazené položky s `null`), takže operátor nemusí přečíslovat
 * nic ručně. Zájezd mimo žebříček je běžný stav, ne chyba.
 *
 * Payload skládá sdílený `buildTripReorderItems` (`packages/shared/src/tripReorder.ts`) —
 * tam žijí i jeho testy, protože administrace vlastní testovací runtime nemá.
 */

/** Doporučená délka žebříčku — vodítko, ne strop (D6). */
const RECOMMENDED_RANKING_LENGTH = 100;

/**
 * Jak dlouho se čeká, než se rozpracovaný žebříček odešle.
 *
 * Klik na ▲/▼ posune zájezd o JEDNU pozici, takže cesta ze 40. místa na 3. je ~37 kliků
 * (očekávaná klávesová alternativa J9). Bez slučování je to 37 zápisů a 37 plných purge
 * sad, jejichž velikost roste s počtem destinací všech položek payloadu — při ~30 slugách
 * ≈ 250 URL na jedno kliknutí. Půl vteřiny je pod prahem, kdy operátor začne čekat na
 * odezvu, a celou takovou sérii přitom sloučí do hrstky zápisů.
 *
 * Nekonečné to není: kdo klika pomaleji než jednou za půl vteřiny, vyrobí zápis za každý
 * klik. Purge je ale best-effort (neúspěšná dávka se jen zaloguje) a katalog se v nejhorším
 * dohojí do `s-maxage=120`, takže cena je zpoždění, ne špatné pořadí.
 */
const SAVE_DEBOUNCE_MS = 500;

function SortableRankedTrip({
  trip,
  position,
  isFirst,
  isLast,
  onMove,
  onRemove,
}: {
  trip: TripWithRelations;
  position: number;
  isFirst: boolean;
  isLast: boolean;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: trip.id,
  });

  const style: React.CSSProperties = {
    transform: DndCSS.Transform.toString(transform as DndTransform | null),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center justify-between gap-3 py-3">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {/* Úchyt je fokusovatelné TLAČÍTKO, takže dnd-kit umí mezerník + šipky (J9). */}
        <button
          type="button"
          className="focus-visible:ring-primary-500 cursor-grab touch-none text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2"
          title={t("trips.order.dragHandle")}
          aria-label={t("trips.order.dragHandle")}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <span className="w-16 shrink-0 text-sm tabular-nums text-gray-500 dark:text-gray-400">
          {t("trips.order.position", { position })}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-gray-900 dark:text-white">{trip.title}</p>
          <p className="truncate text-sm text-gray-500 dark:text-gray-400">
            {trip.destination?.name || trip.slug}
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {/* Druhá klávesová cesta (J9): nezávisí na chování knihovny a jde ověřit E2E. */}
        <button
          type="button"
          onClick={() => onMove(-1)}
          disabled={isFirst}
          title={t("trips.order.moveUp")}
          aria-label={t("trips.order.moveUp")}
          className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent dark:hover:bg-gray-700"
        >
          <ChevronUp className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => onMove(1)}
          disabled={isLast}
          title={t("trips.order.moveDown")}
          aria-label={t("trips.order.moveDown")}
          className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent dark:hover:bg-gray-700"
        >
          <ChevronDown className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onRemove}
          title={t("trips.order.remove")}
          aria-label={t("trips.order.remove")}
          className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function TripOrderPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["trips"],
    queryFn: () => api.get<ApiResponse<TripWithRelations[]>>("/api/admin/trips"),
  });

  const trips = useMemo(() => data?.data ?? [], [data]);
  /** Žebříček podle serveru — zdroj pravdy, dokud operátor nic nepřesune. */
  const serverRankedIds = useMemo(() => rankedTripIds(trips), [trips]);

  /**
   * Rozpracovaný žebříček. Dokud existuje, kreslí se ON a server ho jen dohání; zahodí se
   * až ve chvíli, kdy ho odpověď serveru POTVRDÍ (efekt níž) — ne po úspěšném PATCHi.
   * Mezi odpovědí na PATCH a doručením refetche je totiž okno, ve kterém data ze serveru
   * ještě nesou staré pořadí: kdyby se rozpracovaný stav zahodil hned, žebříček by v tom
   * okně viditelně poskočil zpátky. A při neúspěšném uložení zůstane operátorovi na
   * obrazovce to, co zadal, takže se další pokus odešle celý znovu (včetně vyřazení).
   */
  const [draftRankedIds, setDraftRankedIds] = useState<string[] | null>(null);
  const rankedIds = draftRankedIds ?? serverRankedIds;

  const [saveFailed, setSaveFailed] = useState(false);

  // Plánovač zápisu žije v refech: čte ho `setTimeout` i úklid při odchodu ze stránky,
  // tedy místa, která by na překreslený stav komponenty nedosáhla.
  const draftRef = useRef<string[] | null>(null);
  const serverRankedRef = useRef<string[]>(serverRankedIds);
  const dirtyRef = useRef(false);
  const savingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveRef = useRef<() => void>(() => {});

  useEffect(() => {
    serverRankedRef.current = serverRankedIds;
  }, [serverRankedIds]);

  // Server dohnal rozpracovaný stav → zdrojem pravdy je zase odpověď serveru.
  useEffect(() => {
    if (draftRankedIds && sameRanking(serverRankedIds, draftRankedIds)) {
      draftRef.current = null;
      dirtyRef.current = false;
      setDraftRankedIds(null);
    }
  }, [serverRankedIds, draftRankedIds]);

  /**
   * Zápis. Běží VŽDY nejvýš jeden: přesun, který přijde během letu, se jen poznamená
   * (`dirtyRef`) a odešle se hned po dokončení. Bez toho by dva souběžné PATCHe mohly
   * doputovat mimo pořadí a vyhrál by ten starší — druhý přesun by zmizel bez hlášky.
   */
  const save = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const draft = draftRef.current;
    if (savingRef.current || !draft || !dirtyRef.current) return;

    dirtyRef.current = false;
    savingRef.current = true;
    let saved = false;
    try {
      // Vyřazené položky se dopočítají ze SERVEROVÉHO žebříčku, takže se `sortOrder: null`
      // opakuje v každém dalším pokusu, dokud ho server nepotvrdí (viz `buildTripReorderItems`).
      await api.patch("/api/admin/trips/reorder", {
        items: buildTripReorderItems(draft, serverRankedRef.current),
      });
      saved = true;
    } catch {
      // Obsah se neztrácí — zůstává rozpracovaný a odešle ho další pokus. Automaticky se
      // neopakuje: při trvalé chybě by se z toho stala smyčka.
      dirtyRef.current = true;
    }
    savingRef.current = false;
    setSaveFailed(!saved);
    if (saved) {
      await queryClient.invalidateQueries({ queryKey: ["trips"] });
      if (dirtyRef.current) saveRef.current();
    }
  }, [queryClient]);

  useEffect(() => {
    saveRef.current = () => void save();
  }, [save]);

  // Odchod ze stránky nesmí spolknout poslední přesun, který ještě čeká v debounce.
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      saveRef.current();
    },
    [],
  );

  const tripById = useMemo(() => new Map(trips.map((trip) => [trip.id, trip])), [trips]);
  const rankedSet = useMemo(() => new Set(rankedIds), [rankedIds]);

  const rankedTrips = rankedIds
    .map((id) => tripById.get(id))
    .filter((trip): trip is TripWithRelations => Boolean(trip));

  /**
   * Nezařazené zájezdy v POŘADÍ, které slibuje popisek panelu — od nejnovějšího.
   * Řadí je sdílený `compareUnrankedTrips`, tedy TÝŽ komparátor, jakým je pod žebříček
   * staví katalog (`compareByRank` v API): bez něj tu visel syrový výstup
   * `/api/admin/trips` a operátor viděl jiné pořadí než zákazník na `/zajezdy`.
   *
   * Nefiltruje se podle `sortOrder`, ale podle `rankedSet` — ten drží ROZPRACOVANÝ
   * žebříček, takže zájezd odebraný před uložením spadne sem hned, i když ho server
   * pořád vede jako zařazený.
   *
   * Hledání filtruje i řadí podle shody s názvem (`sortBySearchMatch`); pořadí katalogu
   * je až druhý klíč, takže bez hledání zůstává panel přesně jako dřív.
   */
  const unrankedTrips = sortBySearchMatch(
    trips.filter((trip) => trip.status === "published" && !rankedSet.has(trip.id)),
    search,
    {
      name: (trip) => trip.title,
      other: (trip) => [trip.destination?.name],
      tieBreak: compareUnrankedTrips,
    },
  );

  /**
   * Překreslí žebříček hned a naplánuje uložení. Vyřazení se nikam nezapisuje — plyne
   * z toho, že zájezd v novém žebříčku prostě není (`buildTripReorderItems`).
   */
  const persist = (nextIds: string[]) => {
    draftRef.current = nextIds;
    dirtyRef.current = true;
    setDraftRankedIds(nextIds);
    setSaveFailed(false);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => saveRef.current(), SAVE_DEBOUNCE_MS);
  };

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= rankedIds.length) return;
    persist(arrayMove(rankedIds, index, target));
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="text-primary-500 h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return <div className="card text-error-600 text-center">{t("trips.order.loadError")}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Link
            to="/trips"
            className="mb-1 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("nav.items.trips")}
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("trips.order.title")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">{t("trips.order.subtitle")}</p>
        </div>
        {/* „Ukládám" svítí, dokud server rozpracovaný žebříček nepotvrdí — tedy i během
            debounce a refetche, ne jen po dobu samotného requestu. */}
        {draftRankedIds !== null && !saveFailed && (
          <span className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("trips.order.saving")}
          </span>
        )}
      </div>

      {saveFailed && <div className="card text-error-600">{t("trips.order.saveError")}</div>}

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="card">
          <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            {t("trips.order.ranked")}
          </h2>
          {rankedTrips.length === 0 ? (
            <p className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-gray-500 dark:border-gray-700 dark:text-gray-400">
              {t("trips.order.rankedEmpty")}
            </p>
          ) : (
            <DraggableList
              itemIds={rankedTrips.map((trip) => trip.id)}
              onReorder={(oldIndex, newIndex) => persist(arrayMove(rankedIds, oldIndex, newIndex))}
            >
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {rankedTrips.map((trip, index) => (
                  <SortableRankedTrip
                    key={trip.id}
                    trip={trip}
                    position={index + 1}
                    isFirst={index === 0}
                    isLast={index === rankedTrips.length - 1}
                    onMove={(direction) => move(index, direction)}
                    onRemove={() => persist(rankedIds.filter((id) => id !== trip.id))}
                  />
                ))}
              </div>
            </DraggableList>
          )}
          {rankedTrips.length > RECOMMENDED_RANKING_LENGTH && (
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              {t("trips.order.lengthHint", { total: rankedTrips.length })}
            </p>
          )}
        </div>

        <div className="card">
          <h2 className="mb-1 text-lg font-semibold text-gray-900 dark:text-white">
            {t("trips.order.unranked")}
          </h2>
          <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
            {t("trips.order.unrankedHint")}
          </p>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t("trips.order.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>
          {unrankedTrips.length === 0 ? (
            <p className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-gray-500 dark:border-gray-700 dark:text-gray-400">
              {t("trips.order.unrankedEmpty")}
            </p>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {unrankedTrips.map((trip) => (
                <div key={trip.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-gray-900 dark:text-white">
                      {trip.title}
                    </p>
                    <p className="truncate text-sm text-gray-500 dark:text-gray-400">
                      {trip.destination?.name || trip.slug}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => persist([...rankedIds, trip.id])}
                    title={t("trips.order.add")}
                    aria-label={t("trips.order.add")}
                    className="btn-secondary shrink-0"
                  >
                    <Plus className="h-4 w-4" />
                    {t("trips.order.add")}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
